import React from 'react';
import { t } from '@lingui/core/macro';

import SitterPromo from '@rover/icons/svg/sitter-promo.svg';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import useTheme from '@rover/kibble/themes/useTheme';

import { useRevampCta } from '../../hooks/useRevampCta';
import PromoteProfileButton from '../PromoteProfileButton/PromoteProfileButton';

import EditProfileButton from './EditProfileButton';
import RevampNotApprovedCta from './RevampNotApprovedCta';
import { CTAProps } from './RevampServicesCta';
import RevampServicesCtaCommon from './RevampServicesCtaCommon';

type Props = {
  handleOpenSheet: () => void;
  isOpen: boolean;
};

const RevampServicesCtaSelfView = ({
  priceTransparencyLevel,
  locale,
  petType,
  petCount,
  bookingFeePercentage,
  handleOpenSheet,
  isOpen,
  isApproved,
}: CTAProps & Props): JSX.Element => {
  const { sitterData, serviceType, selectedService } = useRevampCta();
  const theme = useTheme();

  const approvedCta = (
    <Flex data-testid="revamp-services-cta-self-view" flexDirection="column">
      <Badge
        text={t`Your public profile`}
        variant="callout"
        boxProps={{ testID: 'revamp-self-view-badge', alignSelf: 'flex-start', px: '2x', py: '0x' }}
      />

      <Box pt="4x">
        <Heading
          size="400"
          mb="0x"
          sx={{ letterSpacing: '-0.26px', fontSize: ['26px', '26px', '26px'] }}
        >
          {selectedService
            ? t`Contact for ${selectedService.displayName}`
            : t`Contact ${sitterData.shortName}`}{' '}
        </Heading>
      </Box>

      <Flex flexDirection="column" gap="4x" pt="6x">
        <RevampServicesCtaCommon
          priceTransparencyLevel={priceTransparencyLevel}
          locale={locale}
          petCount={petCount}
          petType={petType}
          bookingFeePercentage={bookingFeePercentage}
          handleOpenSheet={handleOpenSheet}
          isOpen={isOpen}
        />

        <Flex
          p="4x"
          gap="3x"
          borderRadius="4px"
          backgroundColor={theme.background.highlight?.toString()}
          alignItems="flex-start"
        >
          <Flex
            flexShrink="0"
            width="24px"
            height="24px"
            alignItems="center"
            justifyContent="center"
          >
            <SitterPromo width="24" height="24" aria-hidden="true" fill={theme.colors.cyan[700]} />
          </Flex>
          <Flex flexDirection="column" gap="1x">
            <Text size="100" fontWeight="semibold">
              {t`Promote your profile`}
            </Text>
            <Text size="100">
              {t`Share your profile to offer pet parents $20 off their first booking with Rover.`}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Flex flexDirection="column" gap="4x" pt="6x">
        <PromoteProfileButton fullWidth isRevampLayout />
        <EditProfileButton fullWidth serviceType={serviceType} />
      </Flex>
    </Flex>
  );

  return isApproved ? approvedCta : <RevampNotApprovedCta />;
};

export default RevampServicesCtaSelfView;
